import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy
} from '@angular/router';
import { ProjectIndexComponent } from '@app/project-index/project-index.component';
import { ProjectMembersComponent } from '@app/project/project-admin/project-members/project-members.component';

export type ReusedComponents =
  | typeof ProjectIndexComponent
  | typeof ProjectMembersComponent;


@Injectable({ providedIn: 'root' })
export class CachingComponentRouteReuseStrategy implements RouteReuseStrategy {
  private _aliveComponents = [ProjectIndexComponent, ProjectMembersComponent];
  private _handles = new Map<
    ReusedComponents,
    DetachedRouteHandle
  >();
  /**
   * Whether the given route should detach for later reuse.
   * Always returns false for `DefaultRouteReuseStrategy`.
   * */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this._aliveComponents.includes(route.component as ReusedComponents);
  }

  /**
   * A no-op; the route is never stored since this strategy never detaches routes for later re-use.
   */
  store(
    route: ActivatedRouteSnapshot,
    detachedTree: DetachedRouteHandle
  ): void {
    this._handles.set(route.component as ReusedComponents, detachedTree);
  }

  /** Returns `false`, meaning the route (and its subtree) is never reattached */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this._handles.has(route.component as ReusedComponents);
  }

  /** Returns `null` because this strategy does not store routes for later re-use. */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this._handles.get(route.component as ReusedComponents) || null;
  }

  /**
   * Determines if a route should be reused.
   * This strategy returns `true` when the future route config and current route config are
   * identical.
   */
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
